import { useJobBrowsingViewModel } from '../viewmodel/useJobBrowsingViewModel';
import Toast from '../../../../components/Toast';
import JobCard from '../components/JobCard';
import { JobType, WorkMode } from '../../../company/JobManagement/model/jobEnums';
import TablePagination from '../../../company/JobManagement/Components/TablePagination';

const JobBrowsingView: React.FC = () => {
  const {
    applicationsPerPage,
    jobs,
    filters,
    loading,
    error,
    total,
    currentPage,
    totalPages,
    handleFilterChange,
    handleSearch,
    setCurrentPage,
    viewJobDetails,
    handleClearFilters,
    isSearchApplied,
    hasActiveFilters,
    toast,
    setToast,
    viewType,
    setViewType,
    setApplicationsPerPage,
    fetchJobs,
  } = useJobBrowsingViewModel();

  return (
    <div className="px-4 sm:px-0 max-w-full overflow-hidden">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div className="sm:col-span-2 lg:col-span-2">
            <input
              type="text"
              placeholder="Search jobs, companies, or keywords..."
              value={filters.query}
              onChange={(e) => handleFilterChange('query', e.target.value)}
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="w-full">
            <input
              type="text"
              placeholder="Location"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="w-full">
            <select
              value={filters.job_type}
              onChange={(e) => handleFilterChange('job_type', e.target.value)}
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All Job Types</option>
              {Object.values(JobType).map((type) => (
                <option key={type} value={type}>
                  {type.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full">
            <select
              value={filters.work_mode}
              onChange={(e) => handleFilterChange('work_mode', e.target.value)}
              className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All Work Modes</option>
              {Object.values(WorkMode).map((mode) => (
                <option key={mode} value={mode}>
                  {mode.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="w-full sm:w-auto">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Min Salary"
                value={filters.salary_min}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  handleFilterChange('salary_min', value);
                }}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleSearch}
              className="px-4 sm:px-6 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 w-full sm:w-auto whitespace-nowrap"
            >
              Search Jobs
            </button>
          </div>

          {hasActiveFilters && isSearchApplied && (
            <button
              onClick={handleClearFilters}
              className="px-4 sm:px-5 py-2 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto whitespace-nowrap"
            >
              ✕ Clear Filters
            </button>
          )}
        </div>
      </div>

      {loading && jobs.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="card p-4 animate-pulse">
              {/* Skeleton content */}
              <div className="h-4 bg-secondary-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      )}

      {/* Job Results */}
      {!loading && jobs.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
          <h2 className="text-base font-semibold text-gray-900 mb-2">No results found</h2>
          <p className="text-sm text-gray-500">
            We couldn’t find any jobs matching your search. Try adjusting your search terms or filters.
          </p>
        </div>
      )}

      {!loading && jobs.length > 0 && (
        <>
          {/* Header + View Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-sm text-gray-700">
              Job{total !== 1 ? 's' : ''} ({total})
            </h2>

            <div className="hidden sm:flex items-center space-x-2">
              <span className="text-sm text-gray-500">View:</span>
              <div className="flex rounded-lg border border-gray-300">
                <button
                  onClick={() => setViewType('list')}
                  className={`px-3 py-1 text-sm font-medium rounded-l-lg ${viewType === 'list'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewType('grid')}
                  className={`px-3 py-1 text-sm font-medium rounded-r-lg ${viewType === 'grid'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Cards Wrapper */}
          <div
            className={
              viewType === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'
                : 'flex flex-col gap-3 sm:gap-4'
            }
          >
            {jobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                onViewDetails={viewJobDetails}
                viewMode={viewType}
              />
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {!loading && total && jobs.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalCount={total}
          rowsPerPage={applicationsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(rows) => {
            setApplicationsPerPage(rows);
            fetchJobs(1, undefined, rows);
          }}
          isGrid={viewType === 'grid'}
        />
      )}
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default JobBrowsingView;